import { useState } from 'react';
import cloneDeep from 'lodash.clonedeep';
import { styled } from '@mui/material/styles';
import TranslateIcon from '@mui/icons-material/TranslateRounded';
import { Input } from '@components/Input';
import { BuilderCard, BuilderCardHeader } from '@components/BuilderCard';
import Button from '@components/Button';
import { Switch } from '@components/Switch';
import DragAndDrop, { SortableItem } from '@components/DragAndDrop';
import { InlineFieldsRow, rowActions } from '@components/FieldWithActions';
import { AIAssistantLoadingWrapper } from '@features/AIAssistant';
import { useTranslation } from '@hooks';
import { Currencies } from '@constants/currencies';
import { MENU_TEXT_LIMITS } from '@constants/menus';
import { MoveIcon } from 'assets/images/icons';
import {
    getLocalizedValue,
    setLocalizedValue,
    hasText,
    createOption,
    areOptionsEnabled,
    buildItemAiContext,
} from '../helpers';
import AttributesSection from './AttributesSection';
import DescriptionAssistant from './DescriptionAssistant';
import MenuImageUpload from './MenuImageUpload';
import MenuLocalizeModal from './MenuLocalizeModal';

// InlineFieldsRow is sized by its grid parent; inside the flex row it must grow itself
const OptionFields = styled(InlineFieldsRow)({
    flex: 1,
    minWidth: 0,
});

const OptionRow = ({
    listeners,
    attributes,
    isView,
    option,
    index,
    count,
    currencySymbol,
    defaultLanguage,
    onUpdate,
    onAdd,
    onDelete,
}) => {
    const { t } = useTranslation();
    const name = getLocalizedValue(option.names, defaultLanguage);

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span
                {...listeners}
                {...attributes}
                style={{ cursor: 'grab', display: 'flex', alignItems: 'center', flexShrink: 0 }}
            >
                <MoveIcon />
            </span>
            <OptionFields
                actions={rowActions({
                    index,
                    count,
                    onAdd,
                    onDelete,
                    disabled: isView,
                    t,
                })}
            >
                <Input
                    inputProps={{
                        value: name,
                        onChange: e =>
                            onUpdate(current => ({
                                ...current,
                                names: setLocalizedValue(current.names, defaultLanguage, e.target.value),
                            })),
                    }}
                    label={`${t('MenusPage.option')} ${index + 1}`}
                    error={!hasText(option.names)}
                    fullWidth
                    required
                    disabled={isView}
                />
                <Input
                    type="number"
                    inputProps={{
                        value: option.priceAmount ?? '',
                        onChange: e =>
                            onUpdate(current => ({
                                ...current,
                                priceAmount: e.target.value === '' ? null : Number(e.target.value),
                            })),
                        min: 0,
                    }}
                    addonComponent={currencySymbol}
                    addonWithoutWrapper
                    label={t('MenusPage.price')}
                    fullWidth
                    disabled={isView}
                />
            </OptionFields>
        </div>
    );
};

const MenuItemCard = ({
    listeners,
    attributes,
    isView,
    item,
    menuId,
    currencyCode,
    defaultLanguage,
    onUpdate,
    onDelete,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localizeOpen, setLocalizeOpen] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);

    const { t } = useTranslation();

    const name = getLocalizedValue(item.names, defaultLanguage);
    const description = getLocalizedValue(item.descriptions, defaultLanguage);
    const descriptionTooLong = (description || '').length > MENU_TEXT_LIMITS.description;
    const options = item.options || [];
    const optionsEnabled = areOptionsEnabled(item);
    const currencySymbol = Currencies.find(c => c.code === currencyCode)?.symbol || currencyCode;
    const hasError =
        !hasText(item.names) ||
        item.priceAmount == null ||
        descriptionTooLong ||
        (optionsEnabled && options.some(option => !hasText(option.names)));

    const onChangeLocalized = (field, value) => {
        onUpdate(current => ({ ...current, [field]: setLocalizedValue(current[field], defaultLanguage, value) }));
    };

    const onChangePrice = value => {
        onUpdate(current => ({ ...current, priceAmount: value === '' ? null : Number(value) }));
    };

    const onToggleOptions = enabled => {
        onUpdate(current => ({
            ...current,
            optionsEnabled: enabled,
            options:
                enabled && !(current.options || []).length ? [createOption(defaultLanguage)] : current.options || [],
        }));
    };

    const onAddOption = () => {
        onUpdate(current => ({ ...current, options: [...(current.options || []), createOption(defaultLanguage)] }));
    };

    const onUpdateOption = (optionId, updater) => {
        onUpdate(current => {
            const optionsCopy = cloneDeep(current.options || []);
            const index = optionsCopy.findIndex(o => o.id === optionId);
            if (index !== -1) {
                optionsCopy[index] = updater(optionsCopy[index]);
            }
            return { ...current, options: optionsCopy };
        });
    };

    const onDeleteOption = optionId => {
        onUpdate(current => ({ ...current, options: (current.options || []).filter(o => o.id !== optionId) }));
    };

    return (
        <BuilderCard isExpanded={isOpen} hasError={hasError}>
            <BuilderCardHeader
                caption={t('MenusPage.item')}
                title={name || t('MenusPage.untitledItem')}
                isExpanded={isOpen}
                hasError={hasError}
                onToggle={() => setIsOpen(prev => !prev)}
                listeners={listeners}
                attributes={attributes}
            />
            {isOpen ? (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gridGap: 12, margin: '8px 0' }}>
                        <Input
                            inputProps={{
                                value: name,
                                onChange: e => onChangeLocalized('names', e.target.value),
                            }}
                            label={t('MenusPage.itemName')}
                            error={!hasText(item.names)}
                            fullWidth
                            required
                            disabled={isView}
                        />
                        <Input
                            type="number"
                            inputProps={{
                                value: item.priceAmount ?? '',
                                onChange: e => onChangePrice(e.target.value),
                                min: 0,
                            }}
                            addonComponent={currencySymbol}
                            addonWithoutWrapper
                            label={t('MenusPage.price')}
                            error={item.priceAmount == null}
                            helperText={item.priceAmount == null ? t('validation.required') : ''}
                            fullWidth
                            required
                            disabled={isView}
                        />
                        <div style={{ position: 'relative', gridColumn: '1 / -1' }}>
                            {aiLoading && <AIAssistantLoadingWrapper />}
                            <Input
                                inputProps={{
                                    value: description,
                                    onChange: e => onChangeLocalized('descriptions', e.target.value),
                                }}
                                label={t('common.description')}
                                fullWidth
                                multiline
                                minRows={2}
                                error={descriptionTooLong}
                                helperText={descriptionTooLong ? t('MenusPage.issueDescriptionLength') : ''}
                                disabled={isView || aiLoading}
                                toolbar={
                                    isView ? undefined : (
                                        <DescriptionAssistant
                                            value={description}
                                            setValue={newValue => onChangeLocalized('descriptions', newValue)}
                                            context={buildItemAiContext(item, defaultLanguage)}
                                            disabled={aiLoading}
                                            setLoading={setAiLoading}
                                        />
                                    )
                                }
                            />
                        </div>
                        <MenuImageUpload
                            label={t('MenusPage.itemPhoto')}
                            menuId={menuId}
                            entityId={item.id}
                            imageUrl={item.imageUrl}
                            isView={isView}
                            onChange={url => onUpdate(current => ({ ...current, imageUrl: url }))}
                        />
                    </div>
                    <AttributesSection
                        isView={isView}
                        attributes={item.attributes}
                        defaultLanguage={defaultLanguage}
                        onChange={attrs => onUpdate(current => ({ ...current, attributes: attrs }))}
                    />
                    <Switch
                        checked={optionsEnabled}
                        handleChange={e => onToggleOptions(e.target.checked)}
                        label={t('MenusPage.addItemOptions')}
                        disabled={isView}
                    />
                    {optionsEnabled ? (
                        <div style={{ margin: '8px 0' }}>
                            <DragAndDrop
                                items={options.map(o => o.id)}
                                data={options}
                                onDragEndCb={result => onUpdate(current => ({ ...current, options: result }))}
                            >
                                {options.map((option, index) => (
                                    <SortableItem key={option.id} id={option.id}>
                                        <OptionRow
                                            isView={isView}
                                            option={option}
                                            index={index}
                                            count={options.length}
                                            currencySymbol={currencySymbol}
                                            defaultLanguage={defaultLanguage}
                                            onUpdate={updater => onUpdateOption(option.id, updater)}
                                            onAdd={onAddOption}
                                            onDelete={() => onDeleteOption(option.id)}
                                        />
                                    </SortableItem>
                                ))}
                            </DragAndDrop>
                        </div>
                    ) : null}
                    <div
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<TranslateIcon />}
                            onClick={() => setLocalizeOpen(true)}
                            disabled={isView}
                        >
                            {t('buttons.localization')}
                        </Button>
                        <Button variant="danger" onClick={onDelete} disabled={isView}>
                            {t('buttons.delete')}
                        </Button>
                    </div>
                </>
            ) : null}
            {localizeOpen ? (
                <MenuLocalizeModal
                    isOpen
                    title={`${t('common.localization')} - ${name}`}
                    names={item.names || []}
                    descriptions={item.descriptions || []}
                    options={optionsEnabled ? options : null}
                    aiContext={buildItemAiContext(item, defaultLanguage)}
                    defaultLanguage={defaultLanguage}
                    onClose={() => setLocalizeOpen(false)}
                    onSave={(names, descriptions, updatedOptions) => {
                        onUpdate(current => ({
                            ...current,
                            names,
                            descriptions,
                            ...(updatedOptions ? { options: updatedOptions } : {}),
                        }));
                        setLocalizeOpen(false);
                    }}
                />
            ) : null}
        </BuilderCard>
    );
};

export default MenuItemCard;
